import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Card, Grid2, Stack, Tab, Tabs } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { useLocales } from '../../../locales';
import {
  selectTourBundleById,
  useCreateTourBundleMutation,
  useUpdateTourBundleMutation,
  useGetTourBundlesQuery,
} from '../../../redux/slices/tourSlice';
import { RootState } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import FormProvider, { RHFTextField, RHFColorpicker } from '../../../components/hook-form';
import Iconify from '../../../components/iconify';
import TourClassVehicleMatrixManagement from './TourClassVehicleMatrixManagement';
import RoleBasedGuard from '../../../auth/RoleBasedGuard';

type Props = {
  isEdit: boolean;
  currentId?: string;
};

type TourBundleFormData = {
  name: string;
  description?: string;
  bundleColor: string;
};

export default function TourBundleEditForm({ isEdit, currentId }: Props) {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState('general');
  const [loadingSend, setLoadingSend] = useState(false);

  useGetTourBundlesQuery();
  const [createTourBundle] = useCreateTourBundleMutation();
  const [updateTourBundle] = useUpdateTourBundleMutation();

  const tourBundle = useSelector((state: RootState) =>
    currentId ? selectTourBundleById(state, currentId) : null
  );

  const newBundleSchema = Yup.object().shape({
    name: Yup.string().required(`${translate('name_required')}`),
    description: Yup.string().optional(),
    bundleColor: Yup.string().required(`${translate('color_required')}`),
  });

  const defaultValues = useMemo<TourBundleFormData>(
    () => ({
      name: tourBundle?.name || '',
      description: tourBundle?.description || '',
      bundleColor: tourBundle?.bundleColor || '#000000',
    }),
    [tourBundle]
  );

  const methods = useForm<TourBundleFormData>({
    resolver: yupResolver(newBundleSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (isEdit && tourBundle) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
  }, [isEdit, tourBundle, reset, defaultValues]);

  const onSubmit = async (data: TourBundleFormData) => {
    setLoadingSend(true);
    try {
      if (isEdit && currentId) {
        await updateTourBundle({
          id: currentId,
          tourBundle: {
            ...tourBundle,
            name: data.name,
            description: data.description,
            bundleColor: data.bundleColor,
          },
        }).unwrap();
        enqueueSnackbar(`${translate('update_successful')}`);
      } else {
        const result = await createTourBundle({
          tourBundle: {
            tourIds: [],
            name: data.name,
            description: data.description,
            bundleColor: data.bundleColor,
          },
        }).unwrap();
        enqueueSnackbar(`${translate('create_successful')}`);
        navigate(PATH_DASHBOARD.basicSetting.tourBundle.view(result.id));
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`${translate('send_failed')}`, { variant: 'error' });
    } finally {
      setLoadingSend(false);
    }
  };

  const tabs = [
    {
      value: 'general',
      label: `${translate('general')}`,
      icon: <Iconify icon="ic:round-receipt" width={20} height={20} />,
      component: (
        <Card sx={{ p: 3 }}>
          <Stack spacing={3}>
            <RHFTextField name="name" label={`${translate('tourBundle.bundleName')}`} />
            <RHFColorpicker name="bundleColor" label={`${translate('tourBundle.color')}`} />
            <RHFTextField
              name="description"
              label={`${translate('tourBundle.description')}`}
              multiline
              rows={4}
            />
          </Stack>
          <RoleBasedGuard roles={['tour-write']}>
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                size="large"
                variant="contained"
                loading={loadingSend && isSubmitting}
                onClick={handleSubmit(onSubmit)}
                sx={{ textTransform: 'none' }}
              >
                {!isEdit ? `${translate('create')}` : `${translate('save_changes')}`}
              </Button>
            </Stack>
          </RoleBasedGuard>
        </Card>
      ),
    },
    ...(isEdit && currentId
      ? [
          {
            value: 'tourClassVehicleMatrix',
            label: `${translate('tourClassVehicleMatrix.tabTitle')}`,
            icon: <Iconify icon="mdi:matrix" width={20} height={20} />,
            component: <TourClassVehicleMatrixManagement tourBundleId={currentId} />,
          },
        ]
      : []),
  ];

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 12 }}>
          <Tabs
            value={currentTab}
            onChange={(event, newValue) => setCurrentTab(newValue)}
            sx={{
              mb: { xs: 3, md: 5 },
            }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                icon={tab.icon}
                value={tab.value}
                iconPosition="start"
              />
            ))}
          </Tabs>

          {tabs.map(
            (tab) =>
              tab.value === currentTab && (
                <Box key={tab.value} sx={{ mt: 3 }}>
                  {tab.component}
                </Box>
              )
          )}
        </Grid2>
      </Grid2>
    </FormProvider>
  );
}
